/**
 * AnalysisTargetChecks — 분석 요청 창(AiRunModal 의 options 칸)에 끼우는 「이번에 만들 것」 체크 넷.
 *
 * 줄마다 데이터 상태만 적는다(데이터 있음 / 비어 있음) — 무엇을 만들지는 체크가 말한다.
 * 오너 지시 2026-09-27: 「은행」→「데이터 있음」, 켠 줄의 「새로 만듦」 뺌, 이어서 정답 값「(③)」과 「그대로 둠」도 뺌.
 * 「비어 있음 · 만들지 않음」(비었는데 끈 줄)만 남아 있다 — 뺄지는 오너 확인 대기.
 * 「있는 데이터라서 넘겼다」가 창 밖에서 조용히 일어나지 않게, 결과를 누르기 전에 읽히게 하는 자리다.
 * 값·초기화는 부르는 쪽이 쥔다(AiRunModal 의 options 규약) — 규칙은 analysisTargets.js.
 * 겉모양은 형제 선택칸(ProjectDetailPanel 의 「전에 걸어 둔 지시」 상자)과 같은 결이다.
 */
import { useId } from 'react';
import PropTypes from 'prop-types';
import { ANALYSIS_TARGET_KEYS, ANALYSIS_TARGET_LABEL, hasAnyTarget, answerOnlyRegen } from './analysisTargets';

function stateText(has, checked) {
  let where;
  if (has == null) where = '데이터 상태 모름';
  else if (has) where = '데이터 있음';
  else where = '비어 있음';
  if (checked || has) return where;
  return `${where} · 만들지 않음`;
}

export default function AnalysisTargetChecks({ targets, onChange, presence = null, disabled = false }) {
  const titleId = useId();
  const none = !hasAnyTarget(targets);
  return (
    <div role="group" aria-labelledby={titleId} className="rounded-xl border border-gray-200 p-3 text-xs text-gray-700">
      <p id={titleId} className="font-medium">이번 분석에서 만들 것</p>
      <div className="mt-1.5 space-y-1">
        {ANALYSIS_TARGET_KEYS.map((k) => {
          const checked = !!targets?.[k];
          return (
            <label key={k} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange({ ...targets, [k]: e.target.checked })}
                disabled={disabled}
                className="accent-primary"
              />
              <span className="w-10 shrink-0 font-medium">{ANALYSIS_TARGET_LABEL[k]}</span>
              <span className="text-gray-500">{stateText(presence?.[k] ?? null, checked)}</span>
            </label>
          );
        })}
      </div>
      {/* 정답만 다시 만들면 AI 는 기존 풀이를 보지 않는다(회신 R4-1, 의도) — 둘이 어긋날 수 있어 함께 권한다 */}
      {answerOnlyRegen(targets, presence) && (
        <p className="mt-1.5 text-[11px] text-amber-700">
          정답을 다시 만들면 풀이도 함께 다시 만드는 것을 권합니다 — AI 는 기존 풀이를 보지 않아 새 정답과 어긋날 수 있습니다.
        </p>
      )}
      {none ? (
        <p role="alert" className="mt-1.5 text-[11px] text-danger">하나 이상 골라야 요청할 수 있습니다.</p>
      ) : (
        <p className="mt-1.5 text-[11px] text-gray-400">
          켠 것은 데이터가 있어도 새로 만듭니다. 승인하기 전에는 데이터가 바뀌지 않습니다.
        </p>
      )}
    </div>
  );
}

AnalysisTargetChecks.propTypes = {
  targets: PropTypes.object.isRequired,   // { concept_ids, difficulty, answer, solution: bool }
  onChange: PropTypes.func.isRequired,    // (nextTargets)
  presence: PropTypes.object,             // 칸별 은행에 값이 있나(true/false/null=모름) — 없으면 전부 모름
  disabled: PropTypes.bool,
};
